import { X, Activity, Zap, Database, CheckCircle, ChevronDown, ChevronRight, FileText, Code, Copy, Check, TrendingUp, MessageSquare } from "lucide-react";
import { useState, useMemo } from "react";
import type { TraceEvent } from "@/types/dashboard";

interface TraceViewerProps {
  questionId: string;
  question: string;
  traces: TraceEvent[];
  sql?: string;
  answer?: string;
  evaluation?: {
    score: number;
    confidence?: string;
    critique?: string;
    reasoning?: string;
  };
  onClose: () => void;
}

// Activity represents a grouped set of events
interface Activity {
  type: 'session' | 'agent_call' | 'agent_response' | 'evaluation' | 'sql_extraction';
  title: string;
  timestamp: string;
  status: 'success' | 'error' | 'info' | 'warning';
  icon: JSX.Element;
  request?: string;
  result?: string;
  state?: string;  // State at the time of request
  error?: string;
  errorPreview?: string; // Condensed error message for collapsed view
  metadata?: {
    isNested?: boolean;
    tool_name?: string;
    tokens_in?: number;
    tokens_out?: number;
    duration_ms?: number;
    sequence?: number;
    [key: string]: any;
  };
}

export function TraceViewer({ questionId, question, traces, sql, answer, evaluation, onClose }: TraceViewerProps) {
  const [activeTab, setActiveTab] = useState<'trace' | 'results'>('trace');
  const [expandedActivities, setExpandedActivities] = useState<Set<number>>(new Set());
  const [activityTabs, setActivityTabs] = useState<Map<number, 'request' | 'result' | 'state'>>(new Map());
  const [copied, setCopied] = useState(false);
  const [expandedStateVars, setExpandedStateVars] = useState<Map<number, Set<string>>>(new Map());

  // Helper function to detect if text contains SQL
  // @ts-ignore - Currently unused but kept for future use
  const containsSQL = (text: string) => {
    return /SELECT|FROM|WHERE|JOIN|INSERT|UPDATE|DELETE/i.test(text);
  };

  // Helper function to format request content nicely
  const formatRequestContent = (content: string) => {
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(content);
      if (parsed.request && typeof parsed.request === 'string') {
        content = parsed.request;
      }
    } catch {
      // Not JSON, use as-is
    }

    // Check if content contains structured agent request format
    if (content.includes('Question:') && content.includes('Discovered Tables:')) {
      const lines: string[] = [];

      // Extract question
      const questionMatch = content.match(/Question:\s*([^\n]+)/);
      if (questionMatch) {
        lines.push('📋 Question');
        lines.push(questionMatch[1]);
        lines.push('');
      }

      // Extract discovered tables
      const tablesSection = content.match(/Discovered Tables:\s*([\s\S]*?)(?=JOIN KEYS:|$)/);
      if (tablesSection) {
        lines.push('📊 Discovered Tables');
        lines.push('');

        // Parse each table entry (numbered)
        const tableMatches = tablesSection[1].matchAll(/(\d+)\.\s+([^:]+):\s+([^\n]+)\n\s+-\s+Type:\s+([^\n]+)\n\s+-\s+Description:\s+([^\n]+)\n\s+-\s+Key columns:\s+([^\n]+)\n\s+-\s+Reason:\s+([^\n]+)/g);

        for (const match of tableMatches) {
          const [, num, tableName, fullPath, type, desc, keyCols, reason] = match;
          lines.push(`${num}. ${tableName.trim()}`);
          lines.push(`   📍 ${fullPath.trim()}`);
          lines.push(`   🏷️  Type: ${type.trim()}`);
          lines.push(`   📝 ${desc.trim()}`);
          lines.push(`   🔑 Columns: ${keyCols.trim()}`);
          lines.push(`   💡 ${reason.trim()}`);
          lines.push('');
        }
      }

      // Extract join keys
      const joinKeysMatch = content.match(/JOIN KEYS:\s*([\s\S]*)/);
      if (joinKeysMatch) {
        lines.push('🔗 Join Keys');
        const joinKeys = joinKeysMatch[1].trim().split('\n-').map(k => k.trim()).filter(k => k);
        joinKeys.forEach(key => {
          lines.push(`   • ${key.replace(/^-\s*/, '')}`);
        });
      }

      return lines.join('\n');
    }

    // Return as-is if no special formatting needed
    return content;
  };

  // Transform traces into activities
  const activities = useMemo<Activity[]>(() => {
    const result: Activity[] = [];

    // Debug logging
    console.log('🔍 Processing traces:', traces.length, 'events');
    console.log('  Sample trace:', traces[0]);
    const toolEvents = traces.filter(t => t.event_name === 'tool_interaction');
    console.log('  Tool events:', toolEvents.length);

    // Sort traces chronologically
    const sortedTraces = [...traces].sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Find first and last timestamps
    const firstTimestamp = sortedTraces.length > 0
      ? sortedTraces[0].timestamp
      : new Date().toISOString();
    const lastTimestamp = sortedTraces.length > 0
      ? sortedTraces[sortedTraces.length - 1].timestamp
      : new Date().toISOString();

    // 1. ALWAYS add User Request first
    result.push({
      type: 'agent_call',
      title: 'User Request',
      timestamp: firstTimestamp,
      status: 'info',
      icon: <Activity className="h-4 w-4" />,
      request: question, // Use the question from props
      metadata: {}
    });

    // 2. Process all trace events (handle both formats: event_name and type)
    for (const event of sortedTraces) {
      const eventType = event.event_name || event.type;

      // Handle historical trace format with type field - Parse SUB-AGENT calls only
      if (eventType === 'agent_response' && event.raw_response && Array.isArray(event.raw_response)) {
        // Build a map of function responses (outputs) by call ID
        const functionResponses = new Map();
        event.raw_response.forEach((agentResponse) => {
          if (agentResponse.content && agentResponse.content.parts) {
            agentResponse.content.parts.forEach((part: any) => {
              if (part.functionResponse) {
                functionResponses.set(part.functionResponse.id, part.functionResponse);
              }
            });
          }
        });

        // Accumulate ADK state as we process responses
        const adkRuntimeState: any = {};

        // Process each agent response for function calls
        event.raw_response.forEach((agentResponse: any) => {
          const parentAgent = agentResponse.author || 'Parent Agent';

          // Accumulate state from stateDelta
          if (agentResponse.actions && agentResponse.actions.stateDelta) {
            Object.assign(adkRuntimeState, agentResponse.actions.stateDelta);
          }

          // Only process responses that have function calls (sub-agents)
          if (agentResponse.content && agentResponse.content.parts) {
            // Extract any text that came BEFORE the function call (parent's reasoning)
            let parentReasoning = '';
            agentResponse.content.parts.forEach((part: any) => {
              if (part.text) {
                parentReasoning += part.text + '\n';
              }
            });

            agentResponse.content.parts.forEach((part: any) => {
              if (part.functionCall) {
                const subAgentName = part.functionCall.name;
                const callId = part.functionCall.id;
                const callArgs = part.functionCall.args || {};

                // Extract the request from callArgs if it exists
                const requestFromArgs = callArgs.request || callArgs.query || callArgs.input;

                // Find the corresponding function response (output) and format nicely
                const functionResponse = functionResponses.get(callId);
                let resultText = `No output captured for ${subAgentName}`;

                if (functionResponse && functionResponse.response) {
                  const resp = functionResponse.response;

                  // Check for SQL query in various common fields
                  if (resp.query || resp.sql || resp.generated_sql) {
                    const sqlQuery = resp.query || resp.sql || resp.generated_sql;
                    resultText = `\`\`\`sql\n${sqlQuery}\n\`\`\``;

                    // Add other fields if present
                    const otherFields = { ...resp };
                    delete otherFields.query;
                    delete otherFields.sql;
                    delete otherFields.generated_sql;

                    if (Object.keys(otherFields).length > 0) {
                      resultText += '\n\n' + Object.entries(otherFields).map(([key, val]) => {
                        return `**${key}:**\n${typeof val === 'string' ? val : JSON.stringify(val, null, 2)}`;
                      }).join('\n\n');
                    }
                  }
                  // Check for result/output fields
                  else if (resp.result) {
                    resultText = typeof resp.result === 'string' ? resp.result : JSON.stringify(resp.result, null, 2);
                  }
                  // Check for tables/schemas
                  else if (resp.tables || resp.schemas || resp.discovered_tables) {
                    const data = resp.tables || resp.schemas || resp.discovered_tables;
                    resultText = `**Discovered:**\n${JSON.stringify(data, null, 2)}`;
                  }
                  // Fallback to JSON
                  else {
                    resultText = JSON.stringify(resp, null, 2);
                  }
                }

                // Request = the actual request argument passed to sub-agent
                const requestText = requestFromArgs
                  ? (typeof requestFromArgs === 'string' ? requestFromArgs : JSON.stringify(requestFromArgs, null, 2))
                  : (parentReasoning.trim() || `Calling: ${subAgentName}`);

                // State = ADK runtime state at the time this sub-agent was called
                const stateSnapshot = { ...adkRuntimeState };

                result.push({
                  type: 'agent_call',
                  title: `🔧 ${subAgentName}`,
                  timestamp: agentResponse.timestamp || event.timestamp,
                  status: functionResponse ? 'success' : 'info',
                  icon: <Zap className="h-4 w-4" />,
                  request: requestText,
                  state: JSON.stringify(stateSnapshot, null, 2),
                  result: resultText,
                  metadata: {
                    agent_name: subAgentName,
                    parent_agent: parentAgent,
                    call_id: callId,
                    is_sub_agent: true
                  }
                });
              }
            });
          }
        });
        continue;
      }

      if (eventType === 'sql_extraction' && event.sql) {
        const eventNum = sortedTraces.indexOf(event) + 1;
        result.push({
          type: 'sql_extraction',
          title: 'SQL Extracted',
          timestamp: event.timestamp,
          status: event.sql_valid ? 'success' : 'warning',
          icon: <Database className="h-4 w-4" />,
          request: `Event: ${eventNum}/${sortedTraces.length}\nSQL Extraction Attempt`,
          state: JSON.stringify({
            sql_found: event.sql_found,
            sql_valid: event.sql_valid,
            timestamp: event.timestamp,
            ready_for_execution: event.sql_valid
          }, null, 2),
          result: `\`\`\`sql\n${event.sql}\n\`\`\``,
          metadata: {
            sql_found: event.sql_found,
            sql_valid: event.sql_valid
          }
        });
        continue;
      }

      if (eventType === 'accuracy_evaluation') {
        const eventNum = sortedTraces.indexOf(event) + 1;
        result.push({
          type: 'evaluation',
          title: `Accuracy Score: ${event.score?.toFixed(1) || 'N/A'}/5.0`,
          timestamp: event.timestamp,
          status: (event.score ?? 0) >= 4 ? 'success' : (event.score ?? 0) >= 3 ? 'warning' : 'error',
          icon: <TrendingUp className="h-4 w-4" />,
          request: `Event: ${eventNum}/${sortedTraces.length}\nEvaluation Request`,
          state: JSON.stringify({
            score: event.score ?? 0,
            confidence: event.confidence || 'N/A',
            timestamp: event.timestamp,
            evaluation_complete: true
          }, null, 2),
          result: event.reasoning || 'No reasoning provided',
          metadata: {
            score: event.score,
            confidence: event.confidence
          }
        });
        continue;
      }

      // Original tool_interaction format
      if (event.event_name === 'tool_interaction' && event.data?.tool_name) {
        const hasError = event.data.result_full?.includes('ERROR') ||
                        event.data.result_preview?.includes('ERROR') ||
                        event.data.result_full?.includes('EXECUTION STATUS: error');

        // Format tool name nicely
        const toolName = event.data.tool_name || 'unknown_tool';
        const toolIcon = toolName.toLowerCase().includes('bigquery') || toolName.toLowerCase().includes('query')
          ? '🔍'
          : '🔧';

        // Extract state information - prefer state AFTER the tool completes
        let stateText = '';
        if (event.data.state_after_response && Object.keys(event.data.state_after_response).length > 0) {
          stateText = JSON.stringify(event.data.state_after_response, null, 2);
        } else if (event.data.state_at_request && Object.keys(event.data.state_at_request).length > 0) {
          stateText = JSON.stringify(event.data.state_at_request, null, 2);
        }

        result.push({
          type: 'agent_call',
          title: `${toolIcon} ${toolName}`,
          timestamp: event.timestamp,
          status: hasError ? 'error' : 'success',
          icon: <Database className="h-4 w-4" />,
          request: typeof event.data.arguments === 'string'
            ? event.data.arguments
            : JSON.stringify(event.data.arguments, null, 2),
          result: event.data.result_full || event.data.result_preview || 'No result',
          state: stateText,  // Add state information
          metadata: {
            tool_name: event.data.tool_name,
            tokens_in: event.data.tokens_in,
            tokens_out: event.data.tokens_out,
            duration_ms: event.data.duration_ms,
            sequence: event.data.sequence,
            isNested: true  // Mark as nested to indent visually
          }
        });
      }

      // Handle state_change events
      if (event.event_name === 'state_change' && event.data) {
        const stateDelta = event.data.state_delta || {};
        const stateAfter = event.data.state_after || {};

        if (Object.keys(stateDelta).length > 0 || Object.keys(stateAfter).length > 0) {
          result.push({
            type: 'agent_response',
            title: '🔄 State Updated',
            timestamp: event.timestamp,
            status: 'info',
            icon: <Activity className="h-4 w-4" />,
            state: JSON.stringify(stateDelta, null, 2),
            result: JSON.stringify(stateAfter, null, 2),
            metadata: {
              state_keys: Object.keys(stateDelta).join(', ')
            }
          });
        }
      }

      // Handle fatal exceptions with comprehensive error details
      if (event.event_name === 'fatal_exception' && event.data) {
        const errorData = event.data;
        const errorType = errorData.error_type || 'Error';
        const errorMessage = errorData.error_message || 'Unknown error';
        const shortMessage = `${errorType}: ${errorMessage}`.substring(0, 100);

        const errorDetails = `🚨 EVALUATION FAILURE REPORT

📅 WHEN:    ${errorData.timestamp || event.timestamp}
📍 WHERE:   ${errorData.location?.file || 'unknown'}:${errorData.location?.line || 0}
           Function: ${errorData.location?.function || 'unknown'}
           Code: ${errorData.location?.code || ''}

⚠️  WHAT:    ${errorType}: ${errorMessage}
❓ WHY:     ${errorData.context?.operation || 'Unknown operation'}

📋 QUESTION: ${errorData.context?.question_id || 'Unknown'}
${errorData.context?.question ? `"${errorData.context.question}"` : ''}

🔧 EXECUTION STATE:
   • Has Session: ${errorData.execution_state?.has_session ? '✓' : '✗'}
   • Has Response: ${errorData.execution_state?.has_response ? '✓' : '✗'}
   • Has Response Data: ${errorData.execution_state?.has_response_data ? '✓' : '✗'}
   • Trace File: ${errorData.execution_state?.trace_file_path || 'N/A'}

📚 STACK TRACE:
${errorData.stack_trace || 'No stack trace available'}`;

        result.push({
          type: 'agent_call',
          title: `💥 ${errorType}`,
          timestamp: event.timestamp,
          status: 'error',
          icon: <X className="h-4 w-4" />,
          errorPreview: shortMessage,  // Show condensed in collapsed view
          error: errorDetails,         // Show full details when expanded
          metadata: {
            error_type: errorData.error_type,
            error_class: errorData.error_class,
            operation: errorData.context?.operation,
            question_id: errorData.context?.question_id,
            isNested: true
          }
        });
      }

      // Handle empty/invalid response errors
      if (event.event_name === 'empty_response_error' && event.data) {
        const errorData = event.data;
        const validationErrors = errorData.validation_errors || [];
        const shortMessage = validationErrors.length > 0
          ? validationErrors[0].substring(0, 80)
          : 'Empty or invalid response from agent';

        const errorDetails = `🚨 EMPTY/INVALID AGENT RESPONSE

📅 Timestamp: ${errorData.timestamp || event.timestamp}

⚠️  Validation Errors:
${validationErrors.map((err: string) => `   • ${err}`).join('\n')}

📊 Response Details:
   • Response Type: ${errorData.response_type || 'unknown'}
   • Response Length: ${errorData.response_length || 0}
   • Extracted Text Length: ${errorData.extracted_text?.length || 0}
   • HTTP Status: ${errorData.http_status || 'unknown'}

📄 Response Preview:
${errorData.raw_response_preview || 'No response data'}

${errorData.extracted_text ? `📝 Extracted Text:\n${errorData.extracted_text}` : ''}`;

        result.push({
          type: 'agent_call',
          title: `🚫 Empty/Invalid Response`,
          timestamp: event.timestamp,
          status: 'error',
          icon: <X className="h-4 w-4" />,
          errorPreview: shortMessage,  // Show condensed in collapsed view
          error: errorDetails,         // Show full details when expanded
          metadata: {
            validation_errors: errorData.validation_errors,
            response_type: errorData.response_type,
            isNested: true
          }
        });
      }

      // Handle content warnings (missing SQL/answer)
      if (event.event_name === 'content_warnings' && event.data) {
        const errorData = event.data;
        const warnings = errorData.warnings || [];
        const shortMessage = warnings.length > 0
          ? warnings.join(', ').substring(0, 80)
          : 'Missing SQL or answer';

        const warningDetails = `⚠️  INCOMPLETE AGENT RESPONSE

📅 Timestamp: ${errorData.timestamp || event.timestamp}

⚠️  Warnings:
${warnings.map((warn: string) => `   • ${warn}`).join('\n')}

📊 Content Analysis:
   • Has SQL: ${errorData.has_sql ? '✓' : '✗'}
   • Has Answer: ${errorData.has_answer ? '✓' : '✗'}
   • Response Length: ${errorData.response_length || 0} chars

The agent responded but did not provide all expected content (SQL query and/or answer).`;

        result.push({
          type: 'agent_call',
          title: `⚠️  Content Warnings`,
          timestamp: event.timestamp,
          status: 'error',
          icon: <Activity className="h-4 w-4" />,
          request: shortMessage,   // Show condensed in collapsed view
          error: warningDetails,   // Show full details when expanded
          metadata: {
            warnings: errorData.warnings,
            has_sql: errorData.has_sql,
            has_answer: errorData.has_answer,
            isNested: true
          }
        });
      }

      // Handle agent errors (extracted from agent_response enhancement)
      if (event.event_name === 'agent_error' && event.data) {
        const errorData = event.data;
        const errorCode = errorData.error_code || 'UNKNOWN_ERROR';
        const shortMessage = errorData.error_message?.split('\n')[0]?.substring(0, 80) || 'Agent execution failed';

        const errorDetails = `🔴 AGENT EXECUTION ERROR

📅 Timestamp: ${event.timestamp}

⚠️  Error: ${errorCode}
💬 Message: ${errorData.error_message || 'Agent execution failed'}
🏁 Finish Reason: ${errorData.finish_reason || 'ERROR'}
📍 Error Index: ${errorData.error_index}`;

        result.push({
          type: 'agent_call',
          title: `🔴 ${errorCode}`,
          timestamp: event.timestamp,
          status: 'error',
          icon: <Activity className="h-4 w-4" />,
          request: shortMessage, // Show condensed in collapsed view
          error: errorDetails,   // Show full details when expanded
          metadata: {
            error_code: errorData.error_code,
            finish_reason: errorData.finish_reason,
            isNested: true
          }
        });
      }

      // Handle general errors
      if (event.event_name === 'error' && event.data) {
        const errorText = event.data.error || JSON.stringify(event.data, null, 2);
        const shortError = errorText.split('\n')[0]?.substring(0, 80) || 'Error occurred';

        const errorDetails = `❌ ERROR

📅 Timestamp: ${event.timestamp}

${errorText}`;

        result.push({
          type: 'agent_call',
          title: `❌ Error`,
          timestamp: event.timestamp,
          status: 'error',
          icon: <Activity className="h-4 w-4" />,
          request: shortError,  // Show condensed in collapsed view
          error: errorDetails,  // Show full details when expanded
          metadata: { isNested: true }
        });
      }

      // Handle session creation - SKIP: not useful in trace view
      // if (event.event_name === 'session_created' && event.data) {
      //   ... hidden ...
      // }

      // Handle agent execution timing - DISABLED (not shown in trace view)
      // This is now skipped as it doesn't provide useful information in the trace
      // if (event.event_name === 'agent_execution' && event.data && event.data.duration_ms != null) {
      //   ...
      // }

      // Handle SQL extraction - SKIP if no SQL found (shown in errors already)
      if (event.event_name === 'sql_extraction' && event.data && event.data.sql_found) {
        const sqlDetails = `🔍 SQL EXTRACTION

📅 Timestamp: ${event.timestamp}

SQL Found: ✓
${event.data.sql ? `\n\`\`\`sql\n${event.data.sql}\n\`\`\`` : ''}`;

        result.push({
          type: 'agent_call',
          title: `🔍 SQL Extraction ✓`,
          timestamp: event.timestamp,
          status: 'success',
          icon: <Database className="h-4 w-4" />,
          result: sqlDetails,
          metadata: {
            sql_found: true
          }
        });
      }

      // Handle accuracy evaluation - Store for later (will be added after Final Response)
      // if (event.event_name === 'accuracy_evaluation' && event.data && event.data.score != null) {
      //   ... will be handled after the loop
      // }
    }

    // 3. Add Final Response (if we have data)
    if (answer || sql) {
      // Try to parse answer as JSON first
      let parsedAnswer = null;
      let plainAnswer = answer;

      if (answer) {
        try {
          // Try to extract JSON from markdown code block if present
          let jsonText = answer;
          const jsonBlockMatch = answer.match(/```json\s*\n([\s\S]*?)\n```/);
          if (jsonBlockMatch) {
            jsonText = jsonBlockMatch[1];
          }

          parsedAnswer = JSON.parse(jsonText);
          if (parsedAnswer && typeof parsedAnswer === 'object') {
            // It's a JSON response
            plainAnswer = parsedAnswer.answer || answer;
          }
        } catch {
          // Not JSON, use as-is
        }
      }

      // Use SQL from parsed JSON if available, otherwise from props
      const finalSql = (parsedAnswer?.sql || sql);

      // Format the final response exactly as the agent provides it
      let finalResponse = '';

      if (finalSql) {
        finalResponse += '```sql\n' + finalSql + '\n```';
        if (plainAnswer) {
          finalResponse += '\n\n';
        }
      }

      if (plainAnswer) {
        finalResponse += plainAnswer;
      }

      result.push({
        type: 'agent_response',
        title: 'Final Response',
        timestamp: lastTimestamp,
        status: 'success',
        icon: <CheckCircle className="h-4 w-4" />,
        result: finalResponse,
        metadata: {}
      });
    }

    // 4. Add Evaluation last (after Final Response)
    const evalEvent = sortedTraces.find(t => t.event_name === 'accuracy_evaluation' && t.data && t.data.score != null);
    if (evalEvent && evalEvent.data) {
      const score = evalEvent.data.score.toFixed(1);
      const evalDetails = `📊 ACCURACY EVALUATION

📅 Timestamp: ${evalEvent.timestamp}

Score: ${score}/5.0
Confidence: ${evalEvent.data.confidence || 'N/A'}

${evalEvent.data.reasoning || 'No reasoning provided'}`;

      result.push({
        type: 'evaluation',
        title: `📊 Evaluation Score: ${score}/5.0`,
        timestamp: evalEvent.timestamp,
        status: evalEvent.data.score >= 4 ? 'success' : evalEvent.data.score >= 3 ? 'info' : 'error',
        icon: <CheckCircle className="h-4 w-4" />,
        result: evalDetails,
        metadata: {
          score: evalEvent.data.score,
          confidence: evalEvent.data.confidence
        }
      });
    }

    return result;
  }, [traces, question, sql, answer]);

  // Count tool calls (nested activities)
  const toolCallCount = useMemo(() => {
    return activities.filter(a => a.metadata?.isNested).length;
  }, [activities]);

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedActivities);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedActivities(newExpanded);
  };

  const setActivityTab = (index: number, tab: 'request' | 'result' | 'state') => {
    const newTabs = new Map(activityTabs);
    newTabs.set(index, tab);
    setActivityTabs(newTabs);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    const time = date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    return `${time}.${ms}`;
  };

  const getStatusColor = (status: Activity['status']) => {
    switch (status) {
      case 'success': return 'border-green-500/30 bg-green-500/10';
      case 'error': return 'border-red-500/30 bg-red-500/10';
      case 'info': return 'border-blue-500/30 bg-blue-500/10';
    }
  };

  const getIconColor = (status: Activity['status']) => {
    switch (status) {
      case 'success': return 'text-green-400';
      case 'error': return 'text-red-400';
      case 'info': return 'text-blue-400';
    }
  };

  // Extract evaluation from traces if not provided
  const evaluationData = useMemo(() => {
    if (evaluation) return evaluation;
    const evalEvent = traces.find(t => t.data?.evaluation_complete);
    if (evalEvent?.data) {
      return {
        score: evalEvent.data.accuracy_score,
        confidence: evalEvent.data.confidence,
        critique: evalEvent.data.critique,
        reasoning: evalEvent.data.reasoning,
      };
    }
    return null;
  }, [evaluation, traces]);

  const getScoreColor = (score: number) => {
    if (score >= 4.5) return "bg-green-500";
    if (score >= 3.5) return "bg-yellow-500";
    if (score >= 2.5) return "bg-orange-500";
    return "bg-red-500";
  };

  const getScoreCategory = (score: number) => {
    if (score >= 4.5) return "Perfect";
    if (score >= 3.5) return "Good";
    if (score >= 2.5) return "Fair";
    return "Poor";
  };

  const copyCondensedTrace = async () => {
    const lines: string[] = [];

    // Header
    lines.push('═══════════════════════════════════════════════════════════════');
    lines.push(`TASK: ${questionId}`);
    lines.push('═══════════════════════════════════════════════════════════════');
    lines.push('');
    lines.push('QUESTION:');
    lines.push(question);
    lines.push('');

    // Parse answer for later use
    let parsedAnswer = null;
    let plainAnswer = answer;
    if (answer) {
      try {
        let jsonText = answer;
        const jsonBlockMatch = answer.match(/```json\s*\n([\s\S]*?)\n```/);
        if (jsonBlockMatch) jsonText = jsonBlockMatch[1];
        parsedAnswer = JSON.parse(jsonText);
        if (parsedAnswer && typeof parsedAnswer === 'object') {
          plainAnswer = parsedAnswer.answer || answer;
        }
      } catch {}
    }
    const finalSql = (parsedAnswer?.sql || sql);

    // ═══════════════════════════════════════════════════════════════
    // RESULTS TAB - SQL & ANSWER
    // ═══════════════════════════════════════════════════════════════
    lines.push('═══════════════════════════════════════════════════════════════');
    lines.push('RESULTS');
    lines.push('═══════════════════════════════════════════════════════════════');
    lines.push('');

    if (finalSql) {
      lines.push('--- SQL QUERY ---');
      lines.push(finalSql);
      lines.push('');
    }

    if (plainAnswer) {
      lines.push('--- ANSWER ---');
      lines.push(plainAnswer);
      lines.push('');
    }

    // ═══════════════════════════════════════════════════════════════
    // EVALUATION
    // ═══════════════════════════════════════════════════════════════
    if (evaluationData) {
      lines.push('═══════════════════════════════════════════════════════════════');
      lines.push('EVALUATION');
      lines.push('═══════════════════════════════════════════════════════════════');
      lines.push('');
      lines.push(`Score: ${evaluationData.score?.toFixed(1) || 'N/A'}/5.0 (${getScoreCategory(evaluationData.score || 0)})`);
      if (evaluationData.confidence) {
        lines.push(`Confidence: ${evaluationData.confidence}`);
      }
      if (evaluationData.reasoning) {
        lines.push('');
        lines.push('--- Reasoning ---');
        lines.push(evaluationData.reasoning);
      }
      if (evaluationData.critique) {
        lines.push('');
        lines.push('--- Critique ---');
        lines.push(evaluationData.critique);
      }
      lines.push('');
    }

    // ═══════════════════════════════════════════════════════════════
    // TRACE TAB - WORKFLOW ACTIVITIES
    // ═══════════════════════════════════════════════════════════════
    const nestedActivities = activities.filter(a => a.metadata?.isNested);
    if (nestedActivities.length > 0) {
      lines.push('═══════════════════════════════════════════════════════════════');
      lines.push('TRACE (WORKFLOW)');
      lines.push('═══════════════════════════════════════════════════════════════');

      nestedActivities.forEach((a, idx) => {
        lines.push('');
        lines.push(`───────────────────────────────────────────────────────────────`);
        lines.push(`[${idx + 1}] ${a.title}`);
        lines.push(`───────────────────────────────────────────────────────────────`);

        if (a.request) {
          const formatted = formatRequestContent(a.request);
          lines.push('');
          lines.push('REQUEST:');
          lines.push(formatted);
        }

        if (a.result) {
          lines.push('');
          lines.push('RESULT:');
          const resultStr = typeof a.result === 'string' ? a.result : JSON.stringify(a.result, null, 2);
          lines.push(resultStr);
        }

        if (a.state) {
          lines.push('');
          lines.push('STATE:');
          try {
            const stateObj = typeof a.state === 'string' ? JSON.parse(a.state) : a.state;
            if (stateObj && typeof stateObj === 'object') {
              Object.entries(stateObj).forEach(([key, value]) => {
                lines.push('');
                lines.push(`▼ ${key}`);
                const valueStr = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
                const indentedValue = valueStr.split('\n').map(line => '  ' + line).join('\n');
                lines.push(indentedValue);
              });
            } else {
              const stateStr = typeof a.state === 'string' ? a.state : JSON.stringify(a.state, null, 2);
              lines.push(stateStr);
            }
          } catch {
            const stateStr = typeof a.state === 'string' ? a.state : JSON.stringify(a.state, null, 2);
            lines.push(stateStr);
          }
        }

        if (a.error) {
          lines.push('');
          lines.push('ERROR:');
          lines.push(a.error);
        }
      });
      lines.push('');
    }

    // ═══════════════════════════════════════════════════════════════
    // RAW TRACE EVENTS (for debugging)
    // ═══════════════════════════════════════════════════════════════
    if (traces.length > 0) {
      lines.push('═══════════════════════════════════════════════════════════════');
      lines.push('RAW TRACE EVENTS');
      lines.push('═══════════════════════════════════════════════════════════════');
      lines.push('');
      lines.push(JSON.stringify(traces, null, 2));
    }

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="glass-modal w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-primary/20">
          <div className="flex items-start justify-between p-6 pb-4">
            <div className="flex-1 pr-4">
              <h2 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
                Task Details
              </h2>
              <p className="text-sm text-muted-foreground mb-1">
                <span className="font-mono text-primary">{questionId}</span>
              </p>
              <p className="text-sm text-foreground/80 line-clamp-2">
                {question}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={copyCondensedTrace}
                className={`p-2 rounded-lg transition-all group ${
                  copied ? 'bg-success/20' : 'hover:bg-primary/20'
                }`}
                title="Copy full trace (all text, plain format)"
              >
                {copied ? (
                  <Check className="h-5 w-5 text-success" />
                ) : (
                  <Copy className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
                )}
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-primary/20 transition-colors"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 px-6">
            <button
              onClick={() => setActiveTab('trace')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'trace'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Activity className="h-4 w-4" />
              Trace
            </button>
            <button
              onClick={() => setActiveTab('results')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'results'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <FileText className="h-4 w-4" />
              Results
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'trace' && (
            <div className="space-y-3">
              {activities.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <Activity className="h-12 w-12 mb-3 animate-pulse" />
                  <p className="text-sm">Waiting for trace events...</p>
                </div>
              ) : (
                activities.map((activity, index) => {
                  const isExpanded = expandedActivities.has(index);
                  // Show tabs for request/result/state, but not for errors (errors go straight to error display)
                  const hasRequestResult = !activity.error && (activity.request || activity.result || activity.state);
                  // Errors are expandable even without tabs
                  const isExpandable = hasRequestResult || activity.error;
                  // Default to 'result' tab if there's no request, otherwise 'request'
                  const defaultTab = activity.request ? 'request' : (activity.result ? 'result' : 'state');
                  const currentTab = activityTabs.get(index) || defaultTab;

                  // Get sequence number for nested tool calls
                  // @ts-ignore - Reserved for future use
                  let toolSequence = 0;
                  if (activity.metadata?.isNested) {
                    toolSequence = activities
                      .slice(0, index + 1)
                      .filter(a => a.metadata?.isNested)
                      .length;
                  }

                  const isNested = activity.metadata?.isNested;

                  return (
                    <div
                      key={index}
                      className={`trace-event ${getStatusColor(activity.status)} ${isNested ? 'ml-8 relative' : ''}`}
                    >
                      {/* Connector line for nested items */}
                      {isNested && (
                        <div className="absolute left-[-24px] top-0 bottom-0 w-0.5 bg-primary/20" />
                      )}
                      {isNested && (
                        <div className="absolute left-[-24px] top-[20px] w-6 h-0.5 bg-primary/20" />
                      )}

                      <div>
                        {/* Clickable title bar - full width and height */}
                        <div
                          className={`flex items-start gap-2 py-2 ${isExpandable ? 'cursor-pointer hover:bg-primary/5 rounded' : ''}`}
                          onClick={() => isExpandable && toggleExpanded(index)}
                        >
                          {isExpandable && (
                            <div className="flex-shrink-0 mt-0.5">
                              {isExpanded ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                            </div>
                          )}
                          <div className={`flex-shrink-0 mt-0.5 ${getIconColor(activity.status)}`}>
                            {activity.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            {/* Title - always visible */}
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-foreground">
                                  {activity.title}
                                </span>
                                <span className="text-[10px] text-muted-foreground font-mono">
                                  {formatTimestamp(activity.timestamp)}
                                </span>
                              </div>
                              {/* Show condensed message for errors when collapsed */}
                              {!isExpanded && activity.errorPreview && (
                                <div className="text-[11px] text-muted-foreground truncate">
                                  {activity.errorPreview}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Expanded content - NOT part of clickable title bar */}
                        {isExpanded && (
                          <div onClick={(e) => e.stopPropagation()}>
                            {/* Expanded view with tabs */}
                            {hasRequestResult && (
                              <div className="mt-3 border border-primary/20 rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
                                {/* Tab Headers */}
                                <div className="flex border-b border-primary/20 bg-primary/5">
                                  {activity.request && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActivityTab(index, 'request');
                                      }}
                                      className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                                        currentTab === 'request'
                                          ? 'bg-purple-500/20 text-purple-300 border-b-2 border-purple-400'
                                          : 'text-muted-foreground hover:text-foreground'
                                      }`}
                                    >
                                      📤 Request
                                    </button>
                                  )}
                                  {activity.result && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActivityTab(index, 'result');
                                      }}
                                      className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                                        currentTab === 'result'
                                          ? 'bg-green-500/20 text-green-300 border-b-2 border-green-400'
                                          : 'text-muted-foreground hover:text-foreground'
                                      }`}
                                    >
                                      📥 Result
                                    </button>
                                  )}
                                  {activity.state && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActivityTab(index, 'state');
                                      }}
                                      className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                                        currentTab === 'state'
                                          ? 'bg-blue-500/20 text-blue-300 border-b-2 border-blue-400'
                                          : 'text-muted-foreground hover:text-foreground'
                                      }`}
                                    >
                                      🔄 State
                                    </button>
                                  )}
                                </div>

                                {/* Tab Content */}
                                <div className="p-4 bg-black/20 max-h-[400px] overflow-y-auto">
                                  {currentTab === 'request' && activity.request && (
                                    (() => {
                                      // Format the request content nicely
                                      const formattedContent = formatRequestContent(activity.request);

                                      // Check if request contains SQL query
                                      const sqlMatch = formattedContent.match(/```sql\n([\s\S]*?)\n```/);
                                      if (sqlMatch) {
                                        const sql = sqlMatch[1];
                                        const otherContent = formattedContent.replace(/```sql\n[\s\S]*?\n```/, '').trim();
                                        return (
                                          <div className="space-y-3">
                                            {otherContent && (
                                              <pre className="text-foreground/80 text-xs whitespace-pre-wrap font-mono">
                                                {otherContent}
                                              </pre>
                                            )}
                                            <div className="rounded border border-purple-500/30 overflow-hidden">
                                              <div className="bg-purple-500/10 px-3 py-1 border-b border-purple-500/30">
                                                <span className="text-xs font-semibold text-purple-300">SQL Query</span>
                                              </div>
                                              <pre className="text-foreground/90 text-xs overflow-x-auto p-3 font-mono bg-black/30">
                                                {sql}
                                              </pre>
                                            </div>
                                          </div>
                                        );
                                      }
                                      return (
                                        <pre className="text-foreground/80 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                                          {formattedContent}
                                        </pre>
                                      );
                                    })()
                                  )}
                                  {currentTab === 'result' && activity.result && (
                                    (() => {
                                      // Convert result to string if it's not already
                                      const resultStr = typeof activity.result === 'string'
                                        ? activity.result
                                        : JSON.stringify(activity.result, null, 2);

                                      // Check if result contains markdown SQL block
                                      const sqlMatch = resultStr.match(/```sql\n([\s\S]*?)\n```/);
                                      if (sqlMatch) {
                                        const sql = sqlMatch[1];
                                        // Remove SQL block AND its label from other content
                                        let otherContent = resultStr
                                          .replace(/SQL Query:\s*\n```sql\n[\s\S]*?\n```/, '')
                                          .replace(/```sql\n[\s\S]*?\n```/, '')
                                          .trim();

                                        return (
                                          <div className="space-y-3">
                                            <div className="rounded border border-blue-500/30 overflow-hidden">
                                              <div className="bg-blue-500/10 px-3 py-1 border-b border-blue-500/30">
                                                <span className="text-xs font-semibold text-blue-300">SQL Query</span>
                                              </div>
                                              <pre className="text-foreground/90 text-xs overflow-x-auto p-3 font-mono bg-black/30">
                                                {sql}
                                              </pre>
                                            </div>
                                            {otherContent && (
                                              <pre className="text-foreground/80 text-xs whitespace-pre-wrap font-mono mt-3">
                                                {otherContent.replace(/^Answer:\s*/i, '')}
                                              </pre>
                                            )}
                                          </div>
                                        );
                                      }
                                      // Regular text
                                      return (
                                        <pre className="text-foreground/80 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                                          {resultStr}
                                        </pre>
                                      );
                                    })()
                                  )}
                                  {currentTab === 'state' && activity.state && (
                                    (() => {
                                      const activityStateVars = expandedStateVars.get(index) || new Set<string>();

                                      const toggleStateVar = (key: string) => {
                                        const newActivityStateVars = new Set(activityStateVars);
                                        if (newActivityStateVars.has(key)) {
                                          newActivityStateVars.delete(key);
                                        } else {
                                          newActivityStateVars.add(key);
                                        }
                                        const newExpandedStateVars = new Map(expandedStateVars);
                                        newExpandedStateVars.set(index, newActivityStateVars);
                                        setExpandedStateVars(newExpandedStateVars);
                                      };

                                      try {
                                        // Try to format as JSON if possible
                                        const stateObj = JSON.parse(activity.state);
                                        return (
                                          <div className="space-y-2">
                                            {Object.entries(stateObj).map(([key, value]) => {
                                              const isExpanded = activityStateVars.has(key);
                                              const valueStr = typeof value === 'string' ? value : JSON.stringify(value, null, 2);

                                              return (
                                                <div key={key} className="rounded border border-blue-500/30 overflow-hidden">
                                                  <div
                                                    className="bg-blue-500/10 px-3 py-2 cursor-pointer hover:bg-blue-500/20 flex items-center gap-2"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      toggleStateVar(key);
                                                    }}
                                                  >
                                                    <span className="text-xs text-blue-300">{isExpanded ? '▼' : '▶'}</span>
                                                    <span className="text-xs font-semibold text-blue-300">{key}</span>
                                                  </div>
                                                  {isExpanded && (
                                                    <pre className="text-foreground/90 text-xs overflow-x-auto p-3 font-mono bg-black/30 whitespace-pre-wrap">
                                                      {valueStr}
                                                    </pre>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      } catch {
                                        // If not valid JSON, display as-is
                                        return (
                                          <pre className="text-foreground/80 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                                            {activity.state}
                                          </pre>
                                        );
                                      }
                                    })()
                                  )}
                                </div>

                                {/* Metadata footer */}
                                {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                                  <div className="px-4 py-2 bg-primary/5 border-t border-primary/10 text-xs text-muted-foreground flex gap-4 flex-wrap">
                                    {activity.metadata.tokens_in !== undefined && (
                                      <span>Tokens: {activity.metadata.tokens_in}→{activity.metadata.tokens_out || 0}</span>
                                    )}
                                    {activity.metadata.duration_ms !== undefined && (
                                      <span>Duration: {activity.metadata.duration_ms}ms</span>
                                    )}
                                    {activity.metadata.sql_found !== undefined && (
                                      <span className={activity.metadata.sql_found ? 'text-green-400' : 'text-red-400'}>
                                        SQL Found: {activity.metadata.sql_found ? '✓' : '✗'}
                                      </span>
                                    )}
                                    {activity.metadata.sql_valid !== undefined && (
                                      <span className={activity.metadata.sql_valid ? 'text-green-400' : 'text-orange-400'}>
                                        SQL Valid: {activity.metadata.sql_valid ? '✓' : '✗'}
                                      </span>
                                    )}
                                    {activity.metadata.score !== undefined && (
                                      <span className="text-blue-400">
                                        Score: {activity.metadata.score}/5.0
                                      </span>
                                    )}
                                    {activity.metadata.confidence && (
                                      <span className="text-purple-400">
                                        Confidence: {activity.metadata.confidence}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Error display - only in expanded view */}
                            {activity.error && (
                              <div className="mt-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <pre className="text-xs text-red-300 font-mono whitespace-pre-wrap overflow-x-auto">
                                  {activity.error}
                                </pre>
                              </div>
                            )}
                            </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'results' && (() => {
            // Extract data from traces (primary source) or fall back to props

            // 1. Find SQL from traces (cleanest source)
            const sqlTraceEvent = traces.find(t => t.type === 'sql_extraction' && t.sql);
            const traceSql = sqlTraceEvent?.sql;

            // 2. Find answer from traces (agent_response with extracted_text)
            const answerTraceEvent = traces.find(t => t.type === 'agent_response' && t.extracted_text);
            const traceAnswer = answerTraceEvent?.extracted_text;

            // 3. Find evaluation from traces
            const evalTraceEvent = traces.find(t => t.type === 'accuracy_evaluation' && t.score != null);
            const traceEvaluation = evalTraceEvent ? {
              score: evalTraceEvent.score,
              confidence: evalTraceEvent.confidence?.toString(),
              reasoning: evalTraceEvent.reasoning,
              critique: evalTraceEvent.critique
            } : null;

            // Use trace data first, then fall back to props
            let rawAnswer = traceAnswer || answer;

            // Extract JUST the answer text (remove SQL blocks and headers)
            let cleanAnswer = rawAnswer;
            if (cleanAnswer) {
              // Remove "SQL QUERY:" section with code block
              cleanAnswer = cleanAnswer.replace(/SQL QUERY:\s*```sql[\s\S]*?```\s*/gi, '');
              // Remove standalone ```sql blocks
              cleanAnswer = cleanAnswer.replace(/```sql[\s\S]*?```\s*/gi, '');
              // Remove "ANSWER:" header if present
              cleanAnswer = cleanAnswer.replace(/^ANSWER:\s*/im, '');
              // Clean up extra whitespace
              cleanAnswer = cleanAnswer.trim();
            }

            // Use SQL from traces (cleanest) or fall back to props
            const finalSql = traceSql || sql;

            // Use evaluation from traces or props
            const finalEvaluation = traceEvaluation || evaluationData;

            return (
            <div className="space-y-6">
              {/* Answer FIRST - this is what the user cares about */}
              {cleanAnswer && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-green-400">
                    <MessageSquare className="h-4 w-4" />
                    Answer
                  </div>
                  <div className="glass-card p-4 border-l-4 border-green-500/50">
                    <div className="text-sm text-foreground/90">
                      {cleanAnswer}
                    </div>
                  </div>
                </div>
              )}

              {/* SQL Query - secondary, collapsible feel */}
              {finalSql && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-blue-400">
                    <Code className="h-4 w-4" />
                    SQL Query
                  </div>
                  <div className="glass-card p-3">
                    <pre className="text-foreground/80 text-xs overflow-x-auto font-mono bg-black/40 rounded p-3 border border-blue-500/20">
                      {finalSql}
                    </pre>
                  </div>
                </div>
              )}

              {/* Evaluation */}
              {finalEvaluation && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <Zap className="h-4 w-4" />
                    Evaluation
                  </div>
                  <div className="glass-card p-4 space-y-4">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground font-medium">Score:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold text-white ${getScoreColor(finalEvaluation.score)}`}>
                        {finalEvaluation.score.toFixed(1)}/5.0 - {getScoreCategory(finalEvaluation.score)}
                      </span>
                      {finalEvaluation.confidence && (
                        <span className="text-xs text-muted-foreground">
                          Confidence: {finalEvaluation.confidence}
                        </span>
                      )}
                    </div>

                    {finalEvaluation.critique && (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-yellow-400">Critique</div>
                        <div className="text-sm text-foreground/80 whitespace-pre-wrap pl-3 border-l-2 border-yellow-500/30">
                          {finalEvaluation.critique}
                        </div>
                      </div>
                    )}

                    {finalEvaluation.reasoning && (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-blue-400">Reasoning</div>
                        <div className="text-sm text-foreground/80 whitespace-pre-wrap pl-3 border-l-2 border-blue-500/30">
                          {finalEvaluation.reasoning}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* No data message */}
              {!finalSql && !cleanAnswer && !finalEvaluation && (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-12">
                  <FileText className="h-12 w-12 mb-3 opacity-30" />
                  <p className="text-sm">No results available yet</p>
                </div>
              )}
            </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-primary/20 bg-card/30">
          {activeTab === 'trace' && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{activities.length} activities • {toolCallCount} tool calls</span>
              <span className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
                Live streaming
              </span>
            </div>
          )}
          {activeTab === 'results' && (() => {
            // Re-extract for footer display
            const sqlTraceEvent = traces.find(t => t.type === 'sql_extraction' && t.sql);
            const hasSql = sqlTraceEvent?.sql || sql;
            const answerTraceEvent = traces.find(t => t.type === 'agent_response' && t.extracted_text);
            const hasAnswer = answerTraceEvent?.extracted_text || answer;
            const evalTraceEvent = traces.find(t => t.type === 'accuracy_evaluation' && t.score != null);
            const hasEval = evalTraceEvent || evaluationData;

            return (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {hasSql && '✓ SQL'}{hasSql && hasAnswer && ' • '}{hasAnswer && '✓ Answer'}{(hasSql || hasAnswer) && hasEval && ' • '}{hasEval && '✓ Evaluation'}
                </span>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

